import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as THREE from 'three';
import {
  MarkerData,
  RectangleData,
} from '../../models/model-viewer/viewer.models';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class MarkerService {
  private markers3D$ = new BehaviorSubject<MarkerData[]>([]);
  public markers3D: Observable<MarkerData[]> = this.markers3D$.asObservable();

  private rectangles2D$ = new BehaviorSubject<RectangleData[]>([]);
  public rectangles2D: Observable<RectangleData[]> =
    this.rectangles2D$.asObservable();

  constructor(private storageService: LocalStorageService) {}

  load3DMarkers(modelUrl: string | null): void {
    const markers = this.storageService.loadMarkers(modelUrl);
    this.markers3D$.next(markers);
  }

  add3DMarker(
    position: THREE.Vector3,
    note: string = '',
    id: string = ''
  ): void {
    const currentMarkers = this.markers3D$.value;
    const newMarker: MarkerData = { position, note, id };
    this.markers3D$.next([...currentMarkers, newMarker]);
  }

  update3DMarker(index: number, note: string, id: string): void {
    const markers = [...this.markers3D$.value];
    if (markers[index]) {
      markers[index].note = note;
      markers[index].id = id;
      this.markers3D$.next(markers);
    }
  }

  removeLast3DMarker(): void {
    const markers = this.markers3D$.value;
    if (markers.length > 0) {
      this.markers3D$.next(markers.slice(0, -1));
    }
  }

  clear3DMarkers(): void {
    this.markers3D$.next([]);
  }

  save3DMarkers(modelUrl: string | null): void {
    this.storageService.saveMarkers(modelUrl, this.markers3D$.value);
  }

  get3DMarkers(): MarkerData[] {
    return this.markers3D$.value;
  }

  load2DRectangles(modelUrl: string | null): void {
    const rectangles = this.storageService.loadRectangles(modelUrl);
    this.rectangles2D$.next(rectangles);
  }

  add2DRectangle(rectangle: RectangleData): void {
    const currentRectangles = this.rectangles2D$.value;
    this.rectangles2D$.next([...currentRectangles, rectangle]);
  }

  update2DRectangle(index: number, note: string, id: string): void {
    const rectangles = [...this.rectangles2D$.value];
    if (rectangles[index]) {
      rectangles[index].note = note;
      rectangles[index].id = id;
      this.rectangles2D$.next(rectangles);
    }
  }

  removeLast2DRectangle(): void {
    const rectangles = this.rectangles2D$.value;
    if (rectangles.length > 0) {
      this.rectangles2D$.next(rectangles.slice(0, -1));
    }
  }

  clear2DRectangles(): void {
    this.rectangles2D$.next([]);
  }

  save2DRectangles(modelUrl: string | null): void {
    this.storageService.saveRectangles(modelUrl, this.rectangles2D$.value);
  }

  get2DRectangles(): RectangleData[] {
    return this.rectangles2D$.value;
  }

  clearAll(modelUrl: string | null): void {
    this.clear3DMarkers();
    this.clear2DRectangles();
    this.storageService.clearData(modelUrl);
  }

  getCount(mode: '3D' | '2D'): number {
    return mode === '3D'
      ? this.markers3D$.value.length
      : this.rectangles2D$.value.length;
  }
}
